import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BlobBot, { BotState } from '../Bots/BlobBot';
import { BotVariant } from '../Bots/motionSystem';
import { useReducedMotion } from '../../animations';
import { ArrowRight } from '../../design/icons';
import {
    PinholeCamera, hexToRgb, mixRgb, clamp, damp, Rgb,
} from '../../lib/camera';
import { createDustField, drawDust, drawRings, drawGate, Mote } from '../../lib/corridor';
import { useThemeTokens } from '../../design/useThemeTokens';
import { setAgentAccent } from '../../lib/useAgentAccent';
import { useRenderGate } from '../../lib/useRenderGate';

export interface Chamber {
    id: string;
    title: string;
    body: string;
    tags: string[];
    color: string;
    state: BotState;
    /** Silhouette — the corridor is inhabited by different agents, not one
        agent repainted six times. */
    variant?: BotVariant;
    /** Direct link to the runnable lab or article that backs this stage. */
    action?: { label: string; to: string };
}

export interface FlightWorldProps {
    chambers: Chamber[];
    /** Viewport-heights of scroll spent per chamber */
    scrollPerChamber?: number;
    /** World units between chamber centres */
    spacing?: number;
}

const SPACING_DEFAULT = 1400;
const RING_SPACING = 260;
const RING_RADIUS = 1080;
const GATE_RADIUS = 760;
const DUST_COUNT = 1400;
const BOT_BASE = 200;      // agents render once at this size, then scale by transform
/** Fallback until the theme tokens resolve on first paint. */
const NEUTRAL_FALLBACK: Rgb = [196, 206, 232];

/**
 * FlightWorld — a corridor the visitor flies down, with an agent living in
 * each chamber.
 *
 * The scene is drawn by `lib/corridor` through a `PinholeCamera`, and the
 * agents are positioned by that same camera. Sharing one projection is what
 * puts them inside the corridor rather than on a layer above it.
 *
 * Content is entirely prop-driven — this component knows nothing about
 * pipelines, articles, or this site. Give it a different `chambers` array and
 * it is a different journey.
 */
const FlightWorld: React.FC<FlightWorldProps> = ({
    chambers,
    scrollPerChamber = 1.3,
    spacing = SPACING_DEFAULT,
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // The corridor is drawn imperatively, so its palette is resolved from the
    // theme rather than baked in — see design/useThemeTokens.
    const [neutralRaw, ringGainRaw, accentMixRaw] =
        useThemeTokens('--film-neutral', '--film-ring-gain', '--film-accent-mix');

    const filmNeutral = React.useMemo<Rgb>(() => {
        const parts = neutralRaw.split(',').map(n => Number(n.trim()));
        return parts.length === 3 && parts.every(Number.isFinite)
            ? (parts as Rgb)
            : NEUTRAL_FALLBACK;
    }, [neutralRaw]);

    const ringGain = Number(ringGainRaw) || 1;
    const accentMix = Number(accentMixRaw) || 0;

    /**
     * Chamber accents, themed. The bots are DOM elements and the rings are
     * canvas, so both have to be handed the same adjusted colour or the flight
     * ends up half-dark-palette on paper.
     */
    const themedChambers = React.useMemo(
        () => chambers.map(c => {
            const rgb: Rgb = accentMix > 0
                ? mixRgb(hexToRgb(c.color), filmNeutral, accentMix)
                : hexToRgb(c.color);
            // The CSS string is for the DOM bots; the tuple is for the canvas.
            // Both come from one computation so they can never disagree.
            return {
                ...c,
                rgb,
                color: `rgb(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])})`,
            };
        }),
        [chambers, filmNeutral, accentMix],
    );
    const botRefs = useRef<(HTMLDivElement | null)[]>([]);
    const gaugeRef = useRef<HTMLSpanElement>(null);

    const dustRef = useRef<Mote[]>([]);
    const camZRef = useRef(0);
    const targetZRef = useRef(0);
    const speedRef = useRef(0);
    const activeRef = useRef(0);

    const [active, setActive] = useState(0);
    const reduced = useReducedMotion();
    // Stop drawing when the tab is hidden or the stage is scrolled away. An
    // ungated rAF loop pins a core for someone who has already left the page.
    const shouldRender = useRenderGate(trackRef);

    const totalZ = (chambers.length - 1) * spacing;
    const wrapDepth = totalZ + 5200;

    // Publish the opening chamber's colour on mount. The per-frame updater only
    // fires on a CHANGE of active chamber, so chamber 0 would otherwise never
    // announce itself and the agent would sit on the persona default.
    useEffect(() => {
        setAgentAccent(themedChambers[0]?.color ?? null);
        return () => setAgentAccent(null);
    }, [themedChambers]);

    useEffect(() => {
        dustRef.current = createDustField({
            count: DUST_COUNT,
            innerRadius: 240,
            outerRadius: 1180,
            depth: wrapDepth,
            seed: 20260905,
        });
    }, [wrapDepth]);

    // ── scroll → camera target ────────────────────────────────────────────
    const readScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        const span = track.offsetHeight - window.innerHeight;
        const y = clamp(window.scrollY - track.offsetTop, 0, Math.max(span, 0));
        const p = span > 0 ? y / span : 0;
        targetZRef.current = p * totalZ;
        if (gaugeRef.current) gaugeRef.current.style.width = `${(p * 100).toFixed(2)}%`;
    }, [totalZ]);

    useEffect(() => {
        readScroll();
        window.addEventListener('scroll', readScroll, { passive: true });
        window.addEventListener('resize', readScroll);
        return () => {
            window.removeEventListener('scroll', readScroll);
            window.removeEventListener('resize', readScroll);
        };
    }, [readScroll]);

    // ── render loop ───────────────────────────────────────────────────────
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || reduced) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const cam = new PinholeCamera();
        const palette = themedChambers.map(c => c.rgb);
        let raf = 0;
        let last = performance.now();

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const rect = canvas.getBoundingClientRect();
            canvas.width = Math.round(rect.width * dpr);
            canvas.height = Math.round(rect.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            cam.setViewport(rect.width, rect.height);
        };
        resize();
        window.addEventListener('resize', resize);

        const frame = (now: number) => {
            if (!shouldRender.current) {
                last = now;                      // do not accumulate a dt debt
                raf = requestAnimationFrame(frame);
                return;
            }
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;

            const prevZ = camZRef.current;
            camZRef.current = damp(camZRef.current, targetZRef.current, 7.5, dt);
            const camZ = camZRef.current;
            speedRef.current = Math.abs(camZ - prevZ) / Math.max(dt, 0.001) * 0.016;

            // A little authored drift, so the shot reads as flown rather than railed.
            const t = camZ / spacing;
            const roll = Math.sin(t * 0.8) * 0.035;
            cam.setPose({
                z: camZ,
                roll,
                sway: { x: Math.sin(t * 0.62) * 46, y: Math.cos(t * 0.47) * 30 },
            });

            // Blend the corridor's hue continuously between chambers, so the
            // colour changes as you travel instead of snapping at a boundary.
            const i = clamp(Math.floor(camZ / spacing), 0, chambers.length - 1);
            const j = Math.min(chambers.length - 1, i + 1);
            const localT = clamp(camZ / spacing - i, 0, 1);
            const accent = mixRgb(palette[i], palette[j], localT);

            ctx.clearRect(0, 0, cam.viewportWidth, cam.viewportHeight);

            drawRings(ctx, cam, accent, camZ, RING_SPACING, RING_RADIUS, roll, ringGain);
            for (let c = 0; c < chambers.length; c++) {
                drawGate(ctx, cam, palette[c], c * spacing, GATE_RADIUS, roll);
            }
            drawDust(ctx, cam, dustRef.current, accent, filmNeutral, wrapDepth, camZ, speedRef.current);

            // Agents share the camera, so they sit at true depth in the corridor.
            for (let c = 0; c < chambers.length; c++) {
                const node = botRefs.current[c];
                if (!node) continue;
                // alternate off-axis so the camera flies past them, not through them
                const offX = c % 2 === 0 ? -180 : 180;
                const p = cam.project(offX, -60, c * spacing);
                if (!p) { node.style.opacity = '0'; continue; }

                const fog = 1 - p.depth / cam.far;
                const passing = clamp((p.depth - cam.near) / 500, 0, 1);
                const opacity = clamp(fog * passing, 0, 1);
                if (opacity <= 0.005) { node.style.opacity = '0'; continue; }

                const scale = Math.min(190 * p.k, 300) / BOT_BASE;
                node.style.opacity = opacity.toFixed(3);
                node.style.transform =
                    `translate3d(${(p.x - BOT_BASE / 2).toFixed(1)}px, ${(p.y - BOT_BASE / 2).toFixed(1)}px, 0) scale(${scale.toFixed(4)})`;
            }

            const nearest = clamp(Math.round(camZ / spacing), 0, chambers.length - 1);
            if (nearest !== activeRef.current) {
                activeRef.current = nearest;
                setActive(nearest);
                // The resident agent adopts the colour of the chamber you are in.
                setAgentAccent(themedChambers[nearest].color);
            }

            raf = requestAnimationFrame(frame);
        };

        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            setAgentAccent(null);
        };
    }, [themedChambers, chambers.length, filmNeutral, reduced, spacing, wrapDepth, shouldRender, ringGain]);

    // ── reduced motion: the same journey, told without the flight ──────────
    if (reduced) {
        return (
            <div style={{ padding: '80px 24px', maxWidth: '820px', margin: '0 auto' }}>
                {themedChambers.map((c, idx) => (
                    <section key={c.id} style={{
                        display: 'flex', gap: '28px', alignItems: 'flex-start', padding: '36px 0',
                        borderTop: idx > 0 ? '1px solid var(--bs-rule)' : undefined,
                    }}>
                        <BlobBot color={c.color} size={88} state={c.state} variant={c.variant ?? 'blob'} />
                        <div>
                            <div style={{
                                fontFamily: 'var(--bs-mono)', fontSize: '11px', letterSpacing: '0.12em',
                                textTransform: 'uppercase', color: 'var(--bs-dim)', marginBottom: '6px',
                            }}>
                                {String(idx + 1).padStart(2, '0')} / {String(themedChambers.length).padStart(2, '0')}
                            </div>
                            <h2 style={{
                                fontFamily: 'var(--bs-serif)', fontStyle: 'italic', fontWeight: 400,
                                fontSize: '30px', margin: '0 0 12px', color: 'var(--film-ink)',
                                letterSpacing: '-0.02em',
                            }}>{c.title}</h2>
                            <p style={{ color: 'var(--film-muted)', lineHeight: 1.7, margin: 0 }}>{c.body}</p>
                            {c.action && (
                                <Link to={c.action.to} className="tap-hit" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    marginTop: '14px', fontFamily: 'var(--bs-mono)', fontSize: '11px',
                                    color: 'var(--bs-accent)', textDecoration: 'none',
                                }}>
                                    {c.action.label} <ArrowRight size={14} />
                                </Link>
                            )}
                        </div>
                    </section>
                ))}
            </div>
        );
    }

    const current = themedChambers[active];

    const jumpToChamber = (index: number) => {
        const track = trackRef.current;
        if (!track) return;
        const span = Math.max(0, track.offsetHeight - window.innerHeight);
        const ratio = chambers.length > 1 ? index / (chambers.length - 1) : 0;
        window.scrollTo({
            top: track.offsetTop + ratio * span,
            behavior: 'smooth',
        });
    };

    return (
        <div
            ref={trackRef}
            style={{ height: `${chambers.length * scrollPerChamber * 100}vh`, position: 'relative' }}
        >
            <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
                <canvas
                    ref={canvasRef}
                    aria-hidden
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
                />

                {themedChambers.map((c, i) => (
                    <div
                        key={c.id}
                        ref={el => { botRefs.current[i] = el; }}
                        aria-hidden
                        style={{
                            position: 'absolute', left: 0, top: 0,
                            width: BOT_BASE, height: BOT_BASE,
                            transformOrigin: '0 0',
                            opacity: 0,
                            pointerEvents: 'none',
                            willChange: 'transform, opacity',
                        }}
                    >
                        <BlobBot color={c.color} size={BOT_BASE} state={c.state} variant={c.variant ?? 'blob'} />
                    </div>
                ))}

                {/* vignette — holds the eye at the vanishing point */}
                <div aria-hidden style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    background: 'radial-gradient(closest-side at 50% 46%, transparent 38%, rgba(var(--film-veil), 0.9) 100%)',
                }} />

                {/* the read: bottom-left, clear of the flight path */}
                <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    padding: 'clamp(24px, 4vw, 60px)',
                    paddingBottom: 'clamp(96px, 13vh, 148px)',
                    background: 'linear-gradient(0deg, rgba(var(--film-veil), 0.95) 10%, rgba(var(--film-veil), 0.5) 56%, transparent 100%)',
                    pointerEvents: 'none',
                }}>
                    <div style={{ maxWidth: '48ch', pointerEvents: 'auto' }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                            marginBottom: '12px', fontFamily: 'var(--bs-mono)', fontSize: '11px',
                            letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--bs-dim)',
                        }}>
                            <span style={{ color: current.color, fontWeight: 600 }}>
                                {String(active + 1).padStart(2, '0')} / {String(themedChambers.length).padStart(2, '0')}
                            </span>
                            <nav aria-label="Pipeline stages" style={{ display: 'inline-flex', gap: '4px', marginLeft: '6px' }}>
                                {themedChambers.map((c, idx) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => jumpToChamber(idx)}
                                        aria-label={`Stage ${idx + 1}: ${c.title}`}
                                        aria-current={idx === active ? 'step' : undefined}
                                        style={{
                                            width: '20px',
                                            height: '6px',
                                            padding: 0,
                                            border: 'none',
                                            background: idx === active ? current.color : 'var(--bs-rule-strong)',
                                            cursor: 'pointer',
                                            transform: idx === active ? 'scaleX(1)' : 'scaleX(0.6)',
                                            transformOrigin: 'center',
                                            transition: 'transform 200ms var(--bs-ease), background 200ms var(--bs-ease)',
                                        }}
                                    />
                                ))}
                            </nav>
                        </div>
                        <h2 style={{
                            fontFamily: 'var(--bs-serif)',
                            fontStyle: 'italic', fontWeight: 400,
                            fontSize: 'clamp(30px, 4.6vw, 58px)',
                            lineHeight: 1.04, letterSpacing: '-0.032em',
                            margin: 0, color: 'var(--film-ink)',
                        }}>{current.title}</h2>
                        <p style={{
                            marginTop: '16px', color: 'var(--film-muted)',
                            fontSize: 'clamp(14px, 1.35vw, 16px)', lineHeight: 1.7,
                            maxWidth: '52ch',
                        }}>{current.body}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '20px' }}>
                            {current.tags.map(tag => (
                                <span key={tag} style={{
                                    fontFamily: 'var(--bs-mono)',
                                    fontSize: '11px', color: 'var(--bs-muted)',
                                    border: '1px solid var(--bs-rule)',
                                    padding: '5px 12px',
                                }}>{tag}</span>
                            ))}
                            {current.action && (
                                <Link
                                    to={current.action.to}
                                    className="tap-hit"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        marginLeft: '6px', padding: '5px 12px',
                                        fontFamily: 'var(--bs-mono)', fontSize: '11px',
                                        color: 'var(--bs-accent)', border: '1px solid var(--bs-accent)',
                                        textDecoration: 'none',
                                    }}
                                >
                                    {current.action.label} <ArrowRight size={13} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>

                {/* depth gauge */}
                <div aria-hidden style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                    background: 'var(--bs-rule)',
                }}>
                    <span ref={gaugeRef} style={{
                        display: 'block', height: '100%', width: '0%',
                        background: current.color, transition: 'background 0.6s',
                    }} />
                </div>
            </div>
        </div>
    );
};

export default FlightWorld;
